import { formatNumberUsage } from '@/utils/function'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { TFunction } from 'i18next'

dayjs.extend(isoWeek)

// Base interface for service analytics data
export interface ServiceAnalyticMonitoring {
    created_at: string
    customer_id: number
    customer_name: string
    id: number
    label: string
    product_group_name: string
    account_manager_codes: number[]
    sale_manager_codes: number[]
    service_entity_name: string
    service_id: number
    updated_at: string
    usage_date: string
    usage_key: string
    usage_unit: string
    usage_value: number
    provider?: string
}

// Time period options for grouping
export type TimePeriod = 'daily' | 'weekly' | 'monthly' | 'yearly'

// Aggregation methods
export type AggregationMethod = 'sum' | 'peak' | 'average' | 'count'

// Unit conversion configuration for different usage keys
export interface UnitConversion {
    factor: number // Conversion factor to apply
    targetUnit: string // Target unit after conversion
    description?: string // Optional description of the conversion
}

// Configuration for data building
export interface DataBuilderConfig {
    timePeriod: TimePeriod
    aggregationMethod: AggregationMethod
    startDate: string // YYYY-MM-DD format
    endDate: string // YYYY-MM-DD format
    usageKeys?: string[] // Filter specific usage keys
    unitConversions?: { [usageKey: string]: UnitConversion } // Unit conversion per usage key
}

// Result structure for built data
export interface BuiltAnalyticsData {
    customer_id: number
    customer_name: string
    product_group_name: string
    periods: {
        [periodKey: string]: {
            [usageKey: string]: {
                value: number // Converted value
                unit: string // Converted unit
                count: number // Number of records aggregated
                originalValue: number // Original value before conversion
                originalUnit: string // Original unit before conversion
            }
        }
    }
}

// Generate period key based on date and time period
export const generatePeriodKey = (
    date: string,
    timePeriod: TimePeriod
): string => {
    const dayDate = dayjs(date)

    switch (timePeriod) {
        case 'daily':
            return dayDate.format('YYYY-MM-DD')
        case 'weekly':
            return `${dayDate.format('YYYY')}-W${dayDate.isoWeek().toString().padStart(2, '0')}`
        case 'monthly':
            return dayDate.format('YYYY-MM')
        case 'yearly':
            return dayDate.format('YYYY')
        default:
            return dayDate.format('YYYY-MM-DD')
    }
}

// Generate all period keys in a date range
export const generatePeriodRange = (
    startDate: string,
    endDate: string,
    timePeriod: TimePeriod
): string[] => {
    const periods: string[] = []
    let current = dayjs(startDate)
    const end = dayjs(endDate)

    while (current.isBefore(end) || current.isSame(end)) {
        periods.push(
            generatePeriodKey(current.format('YYYY-MM-DD'), timePeriod)
        )

        switch (timePeriod) {
            case 'daily':
                current = current.add(1, 'day')
                break
            case 'weekly':
                current = current.add(1, 'week')
                break
            case 'monthly':
                current = current.add(1, 'month')
                break
            case 'yearly':
                current = current.add(1, 'year')
                break
        }
    }

    return [...new Set(periods)] // Remove duplicates
}

// Apply aggregation method to values
export const applyAggregation = (
    values: number[],
    method: AggregationMethod
): number => {
    if (values.length === 0) return 0

    switch (method) {
        case 'sum':
            return values.reduce((sum, val) => sum + val, 0)
        case 'peak':
            return Math.max(...values)
        case 'average':
            return values.reduce((sum, val) => sum + val, 0) / values.length
        case 'count':
            return values.length
        default:
            return values.reduce((sum, val) => sum + val, 0)
    }
}

// Main function to build analytics data
export const buildAnalyticsData = (
    rawData: ServiceAnalyticMonitoring[],
    config: DataBuilderConfig,
    productGroupName?: string,
    showAllProviders?: boolean,
    selectedProviders?: string[]
): BuiltAnalyticsData[] => {
    const { timePeriod, aggregationMethod, startDate, endDate, usageKeys } =
        config

    // Filter data by date range and usage keys if specified
    const filteredData = rawData.filter((item) => {
        const itemDate = dayjs(item.usage_date)
        const inDateRange =
            itemDate.isAfter(dayjs(startDate).subtract(1, 'day')) &&
            itemDate.isBefore(dayjs(endDate).add(1, 'day'))
        const matchesUsageKey = !usageKeys || usageKeys.includes(item.usage_key)

        return inDateRange && matchesUsageKey
    })

    // Check if this is VNIS product group
    const isVNIS = productGroupName === 'VNIS'

    // Group by customer_id
    const customerGroups = filteredData.reduce(
        (acc, item) => {
            if (!acc[item.customer_id]) {
                acc[item.customer_id] = []
            }
            acc[item.customer_id].push(item)
            return acc
        },
        {} as { [customerId: number]: ServiceAnalyticMonitoring[] }
    )

    // Generate all possible periods in the range
    const allPeriods = generatePeriodRange(startDate, endDate, timePeriod)

    // Build result for each customer
    const result: BuiltAnalyticsData[] = Object.entries(customerGroups).map(
        ([customerId, customerData]) => {
            const customer = customerData[0] // Get customer info from first record

            // For VNIS and weekly period, group by period, usage_key, and provider
            if (isVNIS && timePeriod === 'weekly') {
                const periodGroups = customerData.reduce(
                    (acc, item) => {
                        const periodKey = generatePeriodKey(
                            item.usage_date,
                            timePeriod
                        )

                        if (!acc[periodKey]) {
                            acc[periodKey] = {}
                        }

                        if (item.usage_key === 'domains') {
                            // For domains, don't use provider hierarchy
                            if (!acc[periodKey][item.usage_key]) {
                                acc[periodKey][item.usage_key] = []
                            }
                            ;(
                                acc[periodKey][
                                    item.usage_key
                                ] as ServiceAnalyticMonitoring[]
                            ).push(item)
                        } else {
                            // Create hierarchical structure for non-domains: period -> usage_key -> provider
                            if (!acc[periodKey][item.usage_key]) {
                                acc[periodKey][item.usage_key] = {}
                            }

                            const provider = item.provider || ''
                            let groupedProvider: string

                            if (provider === '' || /^\d+$/.test(provider)) {
                                // For empty or numeric providers, group them as 'others'
                                groupedProvider = showAllProviders
                                    ? 'unknown'
                                    : 'others'
                            } else {
                                // For valid providers, use the grouping logic
                                groupedProvider = groupProvider(
                                    provider,
                                    showAllProviders || false,
                                    selectedProviders
                                )
                            }

                            const providerData = acc[periodKey][
                                item.usage_key
                            ] as {
                                [provider: string]: ServiceAnalyticMonitoring[]
                            }
                            if (!providerData[groupedProvider]) {
                                providerData[groupedProvider] = []
                            }
                            providerData[groupedProvider].push(item)
                        }
                        return acc
                    },
                    {} as {
                        [periodKey: string]: {
                            [usageKey: string]:
                                | ServiceAnalyticMonitoring[]
                                | {
                                      [
                                          provider: string
                                      ]: ServiceAnalyticMonitoring[]
                                  }
                        }
                    }
                )

                // Build periods object with VNIS hierarchical structure
                const periods: BuiltAnalyticsData['periods'] = {}

                allPeriods.forEach((periodKey) => {
                    periods[periodKey] = {}

                    // Get all usage keys and providers for this customer
                    const customerUsageKeys = [
                        ...new Set(customerData.map((item) => item.usage_key)),
                    ]

                    customerUsageKeys.forEach((usageKey) => {
                        const usageKeyData = periodGroups[periodKey]?.[usageKey]

                        if (usageKey === 'domains') {
                            // Handle domains as direct array (no provider hierarchy)
                            const periodData =
                                (usageKeyData as ServiceAnalyticMonitoring[]) ||
                                []
                            const originalUnit =
                                periodData[0]?.usage_unit || 'domains'

                            // For domains: sum by day, then return the day with highest count
                            const dailyCounts: number[] = []

                            // Group data by day
                            const dailyGroups = periodData.reduce(
                                (acc, item) => {
                                    const day = dayjs(item.usage_date).format(
                                        'YYYY-MM-DD'
                                    )
                                    if (!acc[day]) {
                                        acc[day] = []
                                    }
                                    acc[day].push(item)
                                    return acc
                                },
                                {} as {
                                    [day: string]: ServiceAnalyticMonitoring[]
                                }
                            )

                            // Sum usage_values per day and get the count (length)
                            Object.values(dailyGroups).forEach((dayData) => {
                                dailyCounts.push(dayData.length) // Return the length (count of records)
                            })

                            // Take the highest length from all days
                            const aggregatedValue =
                                dailyCounts.length > 0
                                    ? Math.max(...dailyCounts)
                                    : 0
                            let finalUnit = originalUnit

                            // Apply unit conversion if specified
                            const conversion =
                                config.unitConversions?.[usageKey]
                            if (conversion && aggregatedValue !== 0) {
                                finalUnit = conversion.targetUnit
                            }

                            periods[periodKey][usageKey] = {
                                value: aggregatedValue,
                                unit: finalUnit,
                                count: periodData.length,
                                originalValue: aggregatedValue,
                                originalUnit: originalUnit,
                            }
                        } else {
                            // Handle other usage keys with provider hierarchy
                            const providerData =
                                (usageKeyData as {
                                    [
                                        provider: string
                                    ]: ServiceAnalyticMonitoring[]
                                }) || {}
                            const providers = Object.keys(providerData)

                            if (providers.length > 0) {
                                providers.forEach((provider) => {
                                    const periodData =
                                        providerData[provider] || []
                                    const values = periodData.map(
                                        (item) => item.usage_value
                                    )
                                    const originalUnit =
                                        periodData[0]?.usage_unit || ''

                                    // Apply standard aggregation for non-domains
                                    let aggregatedValue = applyAggregation(
                                        values,
                                        aggregationMethod
                                    )
                                    let finalUnit = originalUnit

                                    // Apply unit conversion if specified
                                    const conversion =
                                        config.unitConversions?.[usageKey]
                                    if (conversion && aggregatedValue !== 0) {
                                        aggregatedValue =
                                            aggregatedValue * conversion.factor
                                        finalUnit = conversion.targetUnit
                                    }

                                    // Calculate originalValue
                                    const originalValue = applyAggregation(
                                        values,
                                        aggregationMethod
                                    )

                                    // Create hierarchical key structure for VNIS: usageKey.provider
                                    const hierarchicalKey = `${usageKey}.${provider}`
                                    periods[periodKey][hierarchicalKey] = {
                                        value: aggregatedValue,
                                        unit: finalUnit,
                                        count: values.length,
                                        originalValue: originalValue,
                                        originalUnit: originalUnit,
                                    }
                                })
                            }
                        }
                    })
                })

                return {
                    customer_id: parseInt(customerId),
                    customer_name: customer.customer_name,
                    product_group_name: customer.product_group_name,
                    service_entity_name: customer.service_entity_name,
                    periods,
                }
            } else {
                // Standard grouping for non-VNIS or non-weekly periods
                const periodGroups = customerData.reduce(
                    (acc, item) => {
                        const periodKey = generatePeriodKey(
                            item.usage_date,
                            timePeriod
                        )

                        if (!acc[periodKey]) {
                            acc[periodKey] = {}
                        }
                        if (!acc[periodKey][item.usage_key]) {
                            acc[periodKey][item.usage_key] = []
                        }

                        acc[periodKey][item.usage_key].push(item)
                        return acc
                    },
                    {} as {
                        [periodKey: string]: {
                            [usageKey: string]: ServiceAnalyticMonitoring[]
                        }
                    }
                )

                // Build periods object with all periods (including empty ones)
                const periods: BuiltAnalyticsData['periods'] = {}

                allPeriods.forEach((periodKey) => {
                    periods[periodKey] = {}

                    // Get all usage keys for this customer
                    const customerUsageKeys = [
                        ...new Set(customerData.map((item) => item.usage_key)),
                    ]

                    customerUsageKeys.forEach((usageKey) => {
                        const periodData =
                            periodGroups[periodKey]?.[usageKey] || []
                        const values = periodData.map(
                            (item) => item.usage_value
                        )
                        const originalUnit = periodData[0]?.usage_unit || ''

                        // Special aggregation logic for domains
                        let aggregatedValue: number
                        if (usageKey === 'domains') {
                            // For domains: sum all values first, then take peak
                            const totalSum = values.reduce(
                                (sum, val) => sum + val,
                                0
                            )
                            aggregatedValue =
                                totalSum > 0 ? Math.max(...values) : 0
                        } else {
                            // Apply standard aggregation for other usage keys
                            aggregatedValue = applyAggregation(
                                values,
                                aggregationMethod
                            )
                        }
                        let finalUnit = originalUnit

                        // Apply unit conversion if specified
                        const conversion = config.unitConversions?.[usageKey]
                        if (conversion && aggregatedValue !== 0) {
                            aggregatedValue =
                                aggregatedValue * conversion.factor
                            finalUnit = conversion.targetUnit
                        }

                        // Calculate originalValue with same logic
                        let originalValue: number
                        if (usageKey === 'domains') {
                            const totalSum = values.reduce(
                                (sum, val) => sum + val,
                                0
                            )
                            originalValue =
                                totalSum > 0 ? Math.max(...values) : 0
                        } else {
                            originalValue = applyAggregation(
                                values,
                                aggregationMethod
                            )
                        }

                        periods[periodKey][usageKey] = {
                            value: aggregatedValue,
                            unit: finalUnit,
                            count: values.length,
                            originalValue: originalValue,
                            originalUnit: originalUnit,
                        }
                    })
                })

                return {
                    customer_id: parseInt(customerId),
                    customer_name: customer.customer_name,
                    product_group_name: customer?.product_group_name,
                    service_entity_name: customer?.service_entity_name,
                    periods,
                }
            }
        }
    )

    return result.sort((a, b) => a.customer_id - b.customer_id)
}

// Helper function to group providers for VNIS
// always show waf and vncdn
const groupProvider = (
    provider: string,
    showAllProviders: boolean,
    selectedProviders?: string[]
): string => {
    if (provider === 'waf' || provider === 'vncdn') {
        return provider
    }

    if (showAllProviders) {
        return provider
    }

    // If selectedProviders is provided, use custom selection logic
    if (selectedProviders && selectedProviders.length > 0) {
        const lowerProvider = provider.toLowerCase()

        // Check if this provider matches any of the selected providers
        for (const selected of selectedProviders) {
            if (lowerProvider.includes(selected.toLowerCase())) {
                return selected.toLowerCase()
            }
        }

        // If not matched, group as 'others'
        return 'others'
    }

    // Default grouping logic (fallback)
    const lowerProvider = provider.toLowerCase()

    if (lowerProvider.includes('vncdn')) {
        return 'vncdn'
    } else if (lowerProvider.includes('waf')) {
        return 'waf'
    } else {
        return 'others'
    }
}

// Helper function to get column headers from built data
export const getColumnHeaders = (
    builtData: BuiltAnalyticsData[],
    config: DataBuilderConfig
): string[] => {
    if (builtData.length === 0) return []

    const allPeriods = generatePeriodRange(
        config.startDate,
        config.endDate,
        config.timePeriod
    )
    return allPeriods
}

// Helper function to get all usage keys from built data
export const getAllUsageKeys = (builtData: BuiltAnalyticsData[]): string[] => {
    const usageKeys = new Set<string>()

    builtData.forEach((customer) => {
        Object.values(customer.periods).forEach((period) => {
            Object.keys(period).forEach((key) => {
                // For VNIS hierarchical keys (e.g., "traffics.provider1"), extract base usage key
                const baseKey = key.includes('.') ? key.split('.')[0] : key
                usageKeys.add(baseKey)
            })
        })
    })

    return Array.from(usageKeys).sort()
}

// Helper function to get all hierarchical keys (including provider-specific keys for VNIS)
export const getAllHierarchicalKeys = (
    builtData: BuiltAnalyticsData[]
): string[] => {
    const keys = new Set<string>()

    builtData.forEach((customer) => {
        Object.values(customer.periods).forEach((period) => {
            Object.keys(period).forEach((key) => keys.add(key))
        })
    })

    return Array.from(keys).sort()
}

// Helper function to get all unique providers from raw data
export const getAllProviders = (
    rawData: ServiceAnalyticMonitoring[]
): string[] => {
    const providers = new Set<string>()

    rawData.forEach((item) => {
        // remove waf and vncdn
        if (
            item.provider &&
            item.provider !== '' &&
            !/^\d+$/.test(item.provider) &&
            item.provider !== 'waf' &&
            item.provider !== 'vncdn'
        ) {
            providers.add(item.provider)
        }
    })

    return Array.from(providers).sort()
}

// Helper function to format period key for display
export const formatPeriodKey = (
    periodKey: string,
    timePeriod: TimePeriod
): string => {
    switch (timePeriod) {
        case 'daily':
            return dayjs(periodKey).format('DD/MM/YYYY')
        case 'weekly': {
            // 2025-W27
            const [year, week] = periodKey.split('-W')
            const startOfWeek = dayjs()
                .year(Number(year))
                .isoWeek(Number(week))
                .startOf('week')
            const endOfWeek = dayjs()
                .year(Number(year))
                .isoWeek(Number(week))
                .endOf('week')

            return `${startOfWeek.format('DD MMM')} - ${endOfWeek.format('DD MMM, YYYY')}`
        }
        case 'monthly':
            return dayjs(periodKey + '-01').format('MM/YYYY')
        case 'yearly':
            return periodKey
        default:
            return periodKey
    }
}

// Common unit conversion presets
export const createUnitConversions = () => {
    return {
        // No conversion for domains (count remains as is)
        domains: {
            factor: 1,
            targetUnit: 'domains',
            description: 'No conversion needed for domain count',
        },
        // Storage conversion: bytes to GB (1024^-3)
        storage: {
            factor: Math.pow(1024, -3),
            targetUnit: 'GB',
            description: 'Convert bytes to GB',
        },
        local: {
            factor: Math.pow(10, -9),
            targetUnit: 'GB',
            description: 'Convert bytes to GB using decimal conversion',
        },
        global: {
            factor: Math.pow(10, -9),
            targetUnit: 'GB',
            description: 'Convert bytes to GB using decimal conversion',
        },
        tw: {
            factor: Math.pow(10, -9),
            targetUnit: 'GB',
            description: 'Convert bytes to GB using decimal conversion',
        },
        cn: {
            factor: Math.pow(10, -9),
            targetUnit: 'GB',
            description: 'Convert bytes to GB using decimal conversion',
        },
        origin_shield_requests: {
            factor: Math.pow(10, -6),
            targetUnit: 'million requests',
            description: 'No conversion needed for origin shield requests',
        },
        // Origin Shield traffic conversion: bytes to GB (10^-9)
        origin_shield_traffics: {
            factor: Math.pow(10, -9),
            targetUnit: 'GB',
            description: 'Convert bytes to GB using decimal conversion',
        },
        mcdn_requests: {
            factor: Math.pow(10, -6),
            targetUnit: 'requests',
            description: 'No conversion needed for mcdn requests',
        },
        mcdn_traffics: {
            factor: Math.pow(10, -9),
            targetUnit: 'GB',
            description: 'No conversion needed for mcdn traffics',
        },
        total_traffics: {
            factor: Math.pow(10, -9),
            targetUnit: 'GB',
            description: 'No conversion needed for total traffics',
        },
    }
}

// Helper function to get conversion for a usage key
export const getConversionForUsageKey = (
    usageKey: string
): UnitConversion | undefined => {
    const conversions = createUnitConversions()
    return conversions[usageKey as keyof typeof conversions]
}

// Analytics insights interfaces
export interface UsageKeyTrend {
    usageKey: string
    totalValue: number
    averageValue: number
    trend: 'increasing' | 'decreasing' | 'stable' | 'volatile'
    changePercentage: number
    peakPeriod: string
    peakValue: number
    unit: string
}

export interface CustomerInsight {
    customer_id: number
    customer_name: string
    totalUsage: number
    dominantUsageKey: string
    growthRate: number
    trend: 'growing' | 'declining' | 'stable'
}

export interface CustomerSignificantChange {
    customer_id: number
    customer_name: string
    usageKey: string
    previousValue: number
    currentValue: number
    absoluteChange: number
    percentageChange: number
    changeType: 'increase' | 'decrease'
    previousPeriod: string
    currentPeriod: string
    unit: string
}

export interface AnalyticsSummary {
    totalCustomers: number
    totalPeriods: number
    usageKeyTrends: UsageKeyTrend[]
    topCustomers: CustomerInsight[]
    significantChanges: CustomerSignificantChange[]
    overallTrend: 'growth' | 'decline' | 'stable'
    periodComparison: {
        bestPeriod: string
        worstPeriod: string
        averageGrowth: number
    }
    insights: string[]
    recommendations: string[]
}

// Analyze data trends and generate insights
export const analyzeDataTrends = (
    data: BuiltAnalyticsData[],
    config: DataBuilderConfig,
    t: TFunction
): AnalyticsSummary => {
    if (!data || data.length === 0) {
        return {
            totalCustomers: 0,
            totalPeriods: 0,
            usageKeyTrends: [],
            topCustomers: [],
            significantChanges: [],
            overallTrend: 'stable',
            periodComparison: {
                bestPeriod: '',
                worstPeriod: '',
                averageGrowth: 0,
            },
            insights: [],
            recommendations: [],
        }
    }

    const periods = getColumnHeaders(data, config)
    const hierarchicalKeys = getAllHierarchicalKeys(data)

    // Analyze usage key trends (using hierarchical keys for VNIS)
    const usageKeyTrends: UsageKeyTrend[] = hierarchicalKeys.map((key) => {
        const periodValues: { period: string; value: number }[] = []
        let totalValue = 0
        let peakValue = 0
        let peakPeriod = ''
        let unit = ''

        // Collect all values for this key across all customers and periods
        periods.forEach((period) => {
            let periodTotal = 0
            data.forEach((customer) => {
                const usageData = customer.periods[period]?.[key]
                if (usageData) {
                    periodTotal += usageData.value
                    totalValue += usageData.value
                    unit = usageData.unit

                    if (usageData.value > peakValue) {
                        peakValue = usageData.value
                        peakPeriod = period
                    }
                }
            })
            periodValues.push({ period, value: periodTotal })
        })

        // Calculate trend
        const firstValue = periodValues[0]?.value || 0
        const lastValue = periodValues[periodValues.length - 1]?.value || 0
        const changePercentage =
            firstValue === 0 ? 0 : ((lastValue - firstValue) / firstValue) * 100

        // Determine trend type
        let trend: UsageKeyTrend['trend'] = 'stable'
        if (Math.abs(changePercentage) > 20) {
            trend = changePercentage > 0 ? 'increasing' : 'decreasing'
        } else if (Math.abs(changePercentage) > 5) {
            // Check for volatility
            const volatility = calculateVolatility(
                periodValues.map((p) => p.value)
            )
            trend = volatility > 0.3 ? 'volatile' : 'stable'
        }

        return {
            usageKey: key,
            totalValue,
            averageValue: totalValue / periods.length,
            trend,
            changePercentage,
            peakPeriod,
            peakValue,
            unit,
        }
    })

    // Analyze top customers
    const customerInsights: CustomerInsight[] = data
        .map((customer) => {
            let totalUsage = 0
            let dominantUsageKey = ''
            let maxUsage = 0

            // Calculate total usage and find dominant usage key
            Object.values(customer.periods).forEach((period) => {
                Object.entries(period).forEach(([usageKey, usageData]) => {
                    totalUsage += usageData.value
                    if (usageData.value > maxUsage) {
                        maxUsage = usageData.value
                        dominantUsageKey = usageKey
                    }
                })
            })

            // Calculate growth rate (first vs last period)
            const firstPeriodData = customer.periods[periods[0]]
            const lastPeriodData = customer.periods[periods[periods.length - 1]]

            let firstTotal = 0
            let lastTotal = 0

            if (firstPeriodData) {
                Object.values(firstPeriodData).forEach(
                    (data) => (firstTotal += data.value)
                )
            }
            if (lastPeriodData) {
                Object.values(lastPeriodData).forEach(
                    (data) => (lastTotal += data.value)
                )
            }

            const growthRate =
                firstTotal === 0
                    ? 0
                    : ((lastTotal - firstTotal) / firstTotal) * 100
            const trend: CustomerInsight['trend'] =
                growthRate > 10
                    ? 'growing'
                    : growthRate < -10
                      ? 'declining'
                      : 'stable'

            return {
                customer_id: customer.customer_id,
                customer_name: customer.customer_name,
                totalUsage,
                dominantUsageKey,
                growthRate,
                trend,
            }
        })
        .sort((a, b) => b.totalUsage - a.totalUsage)
        .slice(0, 10)

    // Analyze significant changes between periods
    const significantChanges: CustomerSignificantChange[] = []

    if (periods.length >= 2) {
        // Compare each consecutive period pair
        for (let i = 1; i < periods.length; i++) {
            const currentPeriod = periods[i]
            const previousPeriod = periods[i - 1]

            data.forEach((customer) => {
                const currentPeriodData = customer.periods[currentPeriod]
                const previousPeriodData = customer.periods[previousPeriod]

                if (currentPeriodData && previousPeriodData) {
                    hierarchicalKeys.forEach((key) => {
                        const currentUsage = currentPeriodData[key]
                        const previousUsage = previousPeriodData[key]

                        if (
                            currentUsage &&
                            previousUsage &&
                            previousUsage.value > 0
                        ) {
                            const absoluteChange =
                                currentUsage.value - previousUsage.value
                            const percentageChange =
                                ((currentUsage.value - previousUsage.value) /
                                    previousUsage.value) *
                                100

                            // Consider it significant if absolute change is large AND percentage change is > 50%
                            // Or if the absolute change represents a substantial amount
                            const isSignificantPercentage =
                                Math.abs(percentageChange) > 50
                            const isSignificantAbsolute =
                                Math.abs(absoluteChange) >
                                previousUsage.value * 0.3 // 30% of previous value

                            if (
                                isSignificantPercentage &&
                                isSignificantAbsolute
                            ) {
                                significantChanges.push({
                                    customer_id: customer.customer_id,
                                    customer_name: customer.customer_name,
                                    usageKey: key,
                                    previousValue: previousUsage.value,
                                    currentValue: currentUsage.value,
                                    absoluteChange,
                                    percentageChange,
                                    changeType:
                                        absoluteChange > 0
                                            ? 'increase'
                                            : 'decrease',
                                    previousPeriod: formatPeriodKey(
                                        previousPeriod,
                                        config.timePeriod
                                    ),
                                    currentPeriod: formatPeriodKey(
                                        currentPeriod,
                                        config.timePeriod
                                    ),
                                    unit: currentUsage.unit,
                                })
                            }
                        }
                    })
                }
            })
        }
    }

    // Sort by absolute percentage change (most dramatic changes first)
    significantChanges.sort(
        (a, b) => Math.abs(b.percentageChange) - Math.abs(a.percentageChange)
    )

    // Take top 10 most significant changes
    const topSignificantChanges = significantChanges.slice(0, 10)

    // Overall trend analysis
    const overallGrowthRates = usageKeyTrends.map((t) => t.changePercentage)
    const averageGrowth =
        overallGrowthRates.reduce((sum, rate) => sum + rate, 0) /
        overallGrowthRates.length
    const overallTrend: AnalyticsSummary['overallTrend'] =
        averageGrowth > 5 ? 'growth' : averageGrowth < -5 ? 'decline' : 'stable'

    // Period comparison
    const periodTotals = periods.map((period) => {
        const total = data.reduce((sum, customer) => {
            return (
                sum +
                Object.values(customer.periods[period] || {}).reduce(
                    (periodSum, usage) => periodSum + usage.value,
                    0
                )
            )
        }, 0)
        return { period, total }
    })

    const bestPeriod = periodTotals.reduce(
        (max, current) => (current.total > max.total ? current : max),
        periodTotals[0]
    )
    const worstPeriod = periodTotals.reduce(
        (min, current) => (current.total < min.total ? current : min),
        periodTotals[0]
    )

    // Generate insights
    const insights = generateInsights(
        usageKeyTrends,
        customerInsights,
        overallTrend,
        t
    )
    const recommendations = generateRecommendations(
        t,
        usageKeyTrends,
        customerInsights,
        overallTrend
    )

    return {
        totalCustomers: data.length,
        totalPeriods: periods.length,
        usageKeyTrends,
        topCustomers: customerInsights,
        significantChanges: topSignificantChanges,
        overallTrend,
        periodComparison: {
            bestPeriod: formatPeriodKey(bestPeriod.period, config.timePeriod),
            worstPeriod: formatPeriodKey(worstPeriod.period, config.timePeriod),
            averageGrowth,
        },
        insights,
        recommendations,
    }
}

// Helper function to calculate volatility
const calculateVolatility = (values: number[]): number => {
    if (values.length < 2) return 0

    const mean = values.reduce((sum, val) => sum + val, 0) / values.length
    const variance =
        values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        values.length
    const stdDev = Math.sqrt(variance)

    return mean === 0 ? 0 : stdDev / mean
}

// Generate insights based on analysis
const generateInsights = (
    usageKeyTrends: UsageKeyTrend[],
    customerInsights: CustomerInsight[],
    overallTrend: AnalyticsSummary['overallTrend'],
    t: TFunction
): string[] => {
    const insights: string[] = []

    // Overall trend insights
    if (overallTrend === 'growth') {
        insights.push(
            `📈 ${t('services.analytics.overall_usage_is_showing_positive_growth_across_all_metrics')}`
        )
    } else if (overallTrend === 'decline') {
        insights.push(
            `📉 ${t('services.analytics.overall_usage_is_declining_requiring_attention_and_analysis')}`
        )
    } else {
        insights.push(
            `📊 ${t('services.analytics.usage_patterns_are_stable_with_minor_fluctuations')}`
        )
    }

    // Usage key specific insights
    const growingKeys = usageKeyTrends.filter((t) => t.trend === 'increasing')
    const decliningKeys = usageKeyTrends.filter((t) => t.trend === 'decreasing')

    if (growingKeys.length > 0) {
        const topGrowing = growingKeys[0]
        insights.push(
            `🚀 ${t('services.analytics.usage_key_specific_insights', { usageKey: t(topGrowing.usageKey), changePercentage: topGrowing.changePercentage.toFixed(1) })}`
        )
    }

    if (decliningKeys.length > 0) {
        const topDeclining = decliningKeys[0]
        insights.push(
            `⚠️ ${t('services.analytics.usage_key_specific_insights', { usageKey: t(topDeclining.usageKey), changePercentage: Math.abs(topDeclining.changePercentage).toFixed(1) })}`
        )
    }

    // Customer insights
    const growingCustomers = customerInsights.filter(
        (c) => c.trend === 'growing'
    ).length
    const decliningCustomers = customerInsights.filter(
        (c) => c.trend === 'declining'
    ).length

    if (growingCustomers > decliningCustomers) {
        insights.push(
            `👥 ${t('services.analytics.customer_insights', { growingCustomers, decliningCustomers })}`
        )
    }

    // Peak usage insight
    const peakUsageKey = usageKeyTrends.reduce(
        (max, current) => (current.peakValue > max.peakValue ? current : max),
        usageKeyTrends[0]
    )

    if (peakUsageKey) {
        insights.push(
            `📊 ${t('services.analytics.peak_usage_insight', { usageKey: t(peakUsageKey.usageKey), peakValue: formatNumberUsage(peakUsageKey.peakValue), unit: peakUsageKey.unit })}`
        )
    }

    return insights
}

// Generate recommendations based on analysis
const generateRecommendations = (
    t: TFunction,
    usageKeyTrends: UsageKeyTrend[],
    customerInsights: CustomerInsight[],
    overallTrend: AnalyticsSummary['overallTrend']
): string[] => {
    const recommendations: string[] = []

    // Growth recommendations
    if (overallTrend === 'growth') {
        recommendations.push(
            `💡 ${t('services.analytics.consider_scaling_infrastructure_to_handle_increased_usage_demand')}`
        )
        recommendations.push(
            `📋 ${t('services.analytics.monitor_top_customers_for_potential_service_upgrades')}`
        )
    } else if (overallTrend === 'decline') {
        recommendations.push(
            `🔍 ${t('services.analytics.investigate_causes_of_usage_decline_and_customer_satisfaction')}`
        )
        recommendations.push(
            `💬 ${t('services.analytics.engage_with_declining_customers_to_understand_their_needs')}`
        )
    }

    // Volatile usage recommendations
    const volatileKeys = usageKeyTrends.filter((t) => t.trend === 'volatile')
    if (volatileKeys.length > 0) {
        recommendations.push(
            '⚡ ' +
                t(
                    'services.analytics.implement_monitoring_for_volatile_usage_patterns_to_predict_demand'
                )
        )
    }

    // Customer-specific recommendations
    const topCustomer = customerInsights[0]
    if (topCustomer && topCustomer.trend === 'growing') {
        recommendations.push(
            `🎯 ${t('services.analytics.focus_on')} ${topCustomer.customer_name} ${t('services.analytics.as_a_key_growth_opportunity')}`
        )
    }

    // Resource optimization
    const highUsageKeys = usageKeyTrends
        .filter((t) => t.totalValue > 0)
        .sort((a, b) => b.totalValue - a.totalValue)
        .slice(0, 2)

    if (highUsageKeys.length > 0) {
        recommendations.push(
            `🔧 ${t('services.analytics.optimize')} ${t(highUsageKeys[0].usageKey)} ${t('services.analytics.resources_for_cost_efficiency')}`
        )
    }

    return recommendations
}
